import { useState } from 'react'
import { toast } from 'sonner'
import type { ArtikelPosition } from '@/api/warehouse'
import { ApiError, apiFetch, isTransportError } from '@/api/client'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { warehouseErrorMessage } from '@/lib/warehouse-company'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

export function LeiterPaletteKorrektur({ position: p, unit, onClose, onSaved }: {
  position: ArtikelPosition; unit: 'm' | 'St.'; onClose: () => void; onSaved: () => void
}) {
  const [cartons, setCartons] = useState(String(p.cartons ?? ''))
  const [pieces, setPieces] = useState(String(p.piecesPerCarton ?? ''))
  const [rest, setRest] = useState(String(p.restPieces ?? 0))
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [paare, setPaare] = useState(false)
  const [key] = useState(newIdempotencyKey)
  // Ein verlorener Erfolg darf nicht als neuer Versuch mit geänderten Mengen wiederholt werden.
  const [pendingBody, setPendingBody] = useState<string | null>(null)
  const [ambiguous, setAmbiguous] = useState(false)
  const c = Number(cartons), pc = Number(pieces), r = Number(rest)
  const qty = c * pc + r
  const validQty = cartons !== '' && pieces !== '' && rest !== '' && [c, pc, r].every(n => Number.isInteger(n) && n <= 2_147_483_647)
    && c >= 0 && pc >= 1 && r >= 0 && qty <= 2_147_483_647
  const valid = validQty && reason.trim().length >= 5 && reason.trim().length <= 200
  const meter = unit === 'm'
  async function speichern(bestaetigt = false) {
    if (busy || (!valid && !ambiguous)) return
    setBusy(true); setError('')
    const body = pendingBody ?? JSON.stringify({ cartons: c, pieces_per_carton: pc, rest_pieces: r,
      reason: reason.trim(), idempotency_key: key, device_label: 'Grundartikel Web',
      vorher: { qty: p.state === 'verschlossen' ? p.qtyExact : p.qtyCirca, cartons: p.cartons,
        pieces_per_carton: p.piecesPerCarton, state: p.state },
      ...(bestaetigt ? { paare_bestaetigt: true } : {}),
    })
    try {
      await apiFetch(`/warehouse/position/${p.id}/leiter-korrektur/`, { method: 'POST', body })
      toast.success(`${p.paletteNo ?? `Position ${p.id}`}: Menge korrigiert`)
      onSaved()
    } catch (e) {
      setError(warehouseErrorMessage(e))
      const paareFrage = e instanceof ApiError && e.code === 'paare_pruefen'
      // Nur die ausdrückliche Nichtbuchung genau dieses Keys klärt die verlorene
      // Paare-Antwort auf. Sonstige 409/403 behalten den geschützten Versuch.
      const nichtGebucht = paareFrage && e.status === 409 && !e.undecodable && e.details['not_booked_key'] === key
      const unklar = (ambiguous || isTransportError(e)) && !nichtGebucht
      setAmbiguous(unklar)
      setPendingBody(unklar ? body : null)
      setPaare(!unklar && paareFrage)
    } finally { setBusy(false) }
  }
  return <Dialog open onOpenChange={open => { if (!open && !busy && !ambiguous) onClose() }}>
    <DialogContent className="sm:max-w-md" onPointerDownOutside={e => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle>Palette korrigieren · {p.paletteNo ?? `Position ${p.id}`}</DialogTitle>
        <DialogDescription>Physisch nachgezählten Bestand eintragen. Status bleibt „{p.state}“. WE-Auftrag und Plattform/DR-Bestand bleiben unverändert.</DialogDescription>
      </DialogHeader>
      <fieldset disabled={busy || ambiguous} className="space-y-3 disabled:opacity-60">
        <div className="grid grid-cols-3 gap-3">
          <label className="text-sm space-y-1">Kartons<Input type="number" min={0} step={1} value={cartons} onChange={e => { setCartons(e.target.value); setPaare(false) }} /></label>
          <label className="text-sm space-y-1">{meter ? 'Meter/Karton' : 'Stück/Karton'}<Input type="number" min={1} step={1} value={pieces} onChange={e => { setPieces(e.target.value); setPaare(false) }} /></label>
          <label className="text-sm space-y-1">{meter ? 'Rest (Meter)' : 'Rest (Stück)'}<Input type="number" min={0} step={1} value={rest} onChange={e => { setRest(e.target.value); setPaare(false) }} /></label>
        </div>
        <label className="block text-sm space-y-1">Grund<Input value={reason} maxLength={200} placeholder="Mindestens 5 Zeichen" onChange={e => setReason(e.target.value)} /></label>
      </fieldset>
      <div className="rounded-lg border border-line bg-panel-2 p-3 font-medium">Neue Menge: {validQty ? `${qty} ${unit}` : '—'}</div>
      {error && <div role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</div>}
      {ambiguous && <p className="text-sm text-muted">Ergebnis noch unklar. Mit denselben Angaben erneut prüfen.</p>}
      <DialogFooter>
        <Button variant="outline" disabled={busy || ambiguous} onClick={onClose}>Abbrechen</Button>
        {paare ? <Button disabled={busy || !valid} onClick={() => void speichern(true)}>Ja, Stück korrekt</Button>
          : <Button disabled={busy || (!valid && !ambiguous)} onClick={() => void speichern()}>{busy ? 'Speichere…' : ambiguous ? 'Erneut prüfen' : 'Speichern'}</Button>}
      </DialogFooter>
    </DialogContent>
  </Dialog>
}
